import React, { useState, useEffect } from 'react';
import { PenLine, Save, Trash2, Loader2, Eye, Check } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useApp } from '@/context/AppContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function SignatureTab() {
  const { accounts } = useApp();
  const [signatures, setSignatures] = useState({}); // { accountId: text }
  const [editingId, setEditingId] = useState(null);
  const [draftText, setDraftText] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [previewing, setPreviewing] = useState(null);

  // Load all signatures on mount
  useEffect(() => {
    if (!accounts.length) return;
    Promise.all(
      accounts.map(acc =>
        axios.get(`${API}/signatures/${acc.id}`)
          .then(r => ({ id: acc.id, sig: r.data.signature || '' }))
          .catch(() => ({ id: acc.id, sig: '' }))
      )
    ).then(results => {
      const map = {};
      results.forEach(r => { map[r.id] = r.sig; });
      setSignatures(map);
    });
  }, [accounts]);

  const startEdit = (accId) => {
    setEditingId(accId);
    setDraftText(signatures[accId] || '');
    setPreviewing(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setDraftText('');
  };

  const handleSave = async (accId) => {
    setSaving(true);
    try {
      await axios.post(`${API}/signatures/${accId}`, { signature: draftText });
      setSignatures(prev => ({ ...prev, [accId]: draftText }));
      setEditingId(null);
      toast.success('Firma salvata');
    } catch {
      toast.error('Errore nel salvataggio');
    } finally { setSaving(false); }
  };

  const handleDelete = async (accId) => {
    if (!window.confirm('Eliminare la firma?')) return;
    setDeleting(accId);
    try {
      await axios.delete(`${API}/signatures/${accId}`);
      setSignatures(prev => ({ ...prev, [accId]: '' }));
      toast.success('Firma eliminata');
    } catch { toast.error('Errore'); }
    finally { setDeleting(null); }
  };

  if (!accounts.length) {
    return (
      <div className="text-center py-12 text-gray-400">
        <PenLine size={28} className="mx-auto mb-3 opacity-40" />
        <p className="text-sm">Aggiungi prima un account email per configurare le firme</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-500">
        Le firme vengono aggiunte automaticamente nelle nuove email e nelle risposte.
      </p>

      {accounts.map(acc => {
        const sig = signatures[acc.id] || '';
        const isEditing = editingId === acc.id;
        const isPreviewing = previewing === acc.id;

        return (
          <div key={acc.id} className="rounded-xl border overflow-hidden"
            style={{ borderColor: '#D9D2C8', borderLeft: `3px solid ${acc.color}` }}>
            {/* Account header */}
            <div className="flex items-center justify-between px-4 py-3"
              style={{ background: 'white' }}>
              <div className="flex items-center gap-3">
                <span className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                  style={{ background: acc.color }} />
                <span className="text-sm font-medium">{acc.email}</span>
                {sig && !isEditing && (
                  <span className="text-xs px-2 py-0.5 rounded-full font-medium"
                    style={{ background: '#E4F7EE', color: '#2DAA7E' }}>
                    firma attiva
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1.5">
                {sig && !isEditing && (
                  <button
                    data-testid={`preview-sig-${acc.id}`}
                    onClick={() => setPreviewing(isPreviewing ? null : acc.id)}
                    className="p-1.5 text-gray-400 hover:text-gray-700 rounded-lg transition-colors"
                    title="Anteprima">
                    <Eye size={14} />
                  </button>
                )}
                {sig && !isEditing && (
                  <button
                    data-testid={`delete-sig-${acc.id}`}
                    onClick={() => handleDelete(acc.id)}
                    disabled={deleting === acc.id}
                    className="p-1.5 text-gray-400 hover:text-red-500 rounded-lg transition-colors">
                    {deleting === acc.id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                  </button>
                )}
                {!isEditing && (
                  <button
                    data-testid={`edit-sig-${acc.id}`}
                    onClick={() => startEdit(acc.id)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border rounded-lg transition-all hover:bg-gray-50"
                    style={{ borderColor: '#D9D2C8' }}>
                    <PenLine size={12} />
                    {sig ? 'Modifica' : 'Crea firma'}
                  </button>
                )}
              </div>
            </div>

            {/* Preview */}
            {isPreviewing && sig && !isEditing && (
              <div className="px-4 pb-3 border-t" style={{ borderColor: '#F0ECE8', background: '#FAF8F5' }}>
                <p className="text-xs text-gray-400 mb-1.5 pt-2">Anteprima firma:</p>
                <div className="text-xs text-gray-400 mb-1" style={{ fontFamily: 'monospace' }}>--</div>
                <pre className="text-sm leading-relaxed whitespace-pre-wrap text-gray-700"
                  style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {sig}
                </pre>
              </div>
            )}

            {/* Edit form */}
            {isEditing && (
              <div className="px-4 pb-4 border-t space-y-3" style={{ borderColor: '#F0ECE8', background: '#FAF8F5' }}>
                <p className="text-xs text-gray-400 mt-3">
                  Scrivi la firma per <strong>{acc.email}</strong>. Sarà inserita automaticamente sotto il tuo testo.
                </p>
                <textarea
                  data-testid={`sig-textarea-${acc.id}`}
                  value={draftText}
                  onChange={e => setDraftText(e.target.value)}
                  placeholder={`Mario Rossi\nMariani di Impruneta\nTel: +39 055 123456\nwww.mariani.it`}
                  rows={5}
                  className="w-full px-3 py-2.5 text-sm rounded-lg border resize-y focus:outline-none transition-all"
                  style={{ borderColor: '#D9D2C8', background: 'white', fontFamily: 'Manrope, sans-serif' }}
                  onFocus={e => e.target.style.borderColor = '#E2725B'}
                  onBlur={e => e.target.style.borderColor = '#D9D2C8'}
                />
                {draftText && (
                  <div className="rounded-lg p-3" style={{ background: 'white', border: '1px dashed #D9D2C8' }}>
                    <p className="text-xs text-gray-400 mb-1">Anteprima:</p>
                    <div className="text-xs text-gray-400 font-mono">--</div>
                    <pre className="text-sm text-gray-600 whitespace-pre-wrap" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      {draftText}
                    </pre>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <button
                    data-testid={`save-sig-${acc.id}`}
                    onClick={() => handleSave(acc.id)}
                    disabled={saving}
                    className="flex items-center gap-2 px-5 py-2 text-sm font-medium text-white rounded-xl transition-all"
                    style={{ background: '#E2725B', opacity: saving ? 0.7 : 1 }}>
                    {saving ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
                    {saving ? 'Salvataggio...' : 'Salva firma'}
                  </button>
                  <button
                    onClick={cancelEdit}
                    className="px-4 py-2 text-sm border rounded-xl hover:bg-gray-50 transition-all"
                    style={{ borderColor: '#D9D2C8' }}>
                    Annulla
                  </button>
                </div>
              </div>
            )}

            {/* No signature placeholder */}
            {!sig && !isEditing && (
              <div className="px-4 pb-3 pt-1 border-t" style={{ borderColor: '#F0ECE8', background: '#FAF8F5' }}>
                <p className="text-xs text-gray-400">Nessuna firma configurata</p>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
